import type { PendingAdyenPayment } from '@/helpers/embedPayment';
import { createContext, useContext } from 'react';

/**
 * `opening`: the waiting popup is up and the checkout request is in flight.
 * `open`: the popup has the pay page. `reconciling`: the popup closed without a
 * report and the checkout status is being polled. The three terminals are held
 * until the checkout has reacted and acknowledged them.
 */
export type WidgetPaymentState = 'idle' | 'opening' | 'open' | 'reconciling' | 'complete' | 'error' | 'aborted';

export type WidgetPaymentResult = { errorCode?: string; errorMessage?: string };

/**
 * `opened`: a waiting popup is up, submit the checkout. `blocked`: nothing opened,
 * tell the visitor. `busy`: a popup is live, it was focused instead. `pending`: the
 * previous submit has not settled and no popup is live, so there is nothing to
 * wait in.
 */
export type PreOpenOutcome = 'opened' | 'blocked' | 'busy' | 'pending';

export type WidgetPaymentContextValue = {
  state: WidgetPaymentState;
  pending: PendingAdyenPayment | null;
  result?: WidgetPaymentResult;
  /** A submit that pre-opened a popup has not settled yet. */
  submitPending: boolean;
  /**
   * Opens the waiting popup. Synchronous — call it directly in the click handler,
   * before the checkout request (Safari only honours `window.open` within the gesture).
   */
  preOpen: () => PreOpenOutcome;
  /** The checkout returned a session: hand the popup the pay URL. */
  handover: (pending: PendingAdyenPayment) => void;
  /** The submit settled: close the waiting popup if it was never handed the pay URL. */
  closeIfNotHandedOver: () => void;
  /** The checkout has reacted to a terminal state; the next payment starts from idle. */
  acknowledge: () => void;
  focusPopup: () => void;
};

/**
 * Provided by `WidgetPayment` at the widget's checkout boundary. The default is
 * inert so the checkout components render unchanged on the marketplace: nothing is
 * pre-opened there, and `opened` lets a caller fall through to the submit.
 * Keep this module light — it is imported by shared checkout code.
 */
export const WidgetPaymentContext = createContext<WidgetPaymentContextValue>({
  state: 'idle',
  pending: null,
  submitPending: false,
  preOpen: () => 'opened',
  handover: () => {},
  closeIfNotHandedOver: () => {},
  acknowledge: () => {},
  focusPopup: () => {},
});

export const useWidgetPayment = () => useContext(WidgetPaymentContext);
